import { useState, useEffect, useRef } from "react";
import api from "../services/api";
import { Table, SearchBar } from "@tw/tw-common-js";
import RoleEdit from "../components/other/RoleEdit";
import { useAuth } from "../context/useAuth";

export default function RoleManagement() {
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState(null);
  const [showModalEdit, setShowModalEdit] = useState(false);
  const [showActions, setShowActions] = useState(false);
  const [searchText, setSearchText] = useState("");
  const actionsDropdownRef = useRef(null);
  const { privilegeLabels } = useAuth();

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const roles = await api.getRolesWithCount();
        if (roles.status === 200) {
          setRoles(roles.data);
        }
      } catch (error) {
        console.error("Error fetching roles", error);
      }
    };

    fetchRoles();
  }, []);

  // Close Actions dropdown when clicking outside
  useEffect(() => {
    if (!showActions) return;
    function handleClickOutside(event) {
      if (actionsDropdownRef.current && !actionsDropdownRef.current.contains(event.target)) {
        setShowActions(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showActions]);

  const config = {
    columns: [
      {
        key: "name",
        header: "Role Name",
        className: "col-small",
        template: (role) => <span className="main-row">{role.name || "No Name"}</span>,
      },
      {
        key: "privileges",
        header: "Privileges",
        className: "col-medium-scroll",
        template: (role) => {
          const privileges = Array.isArray(role.privileges) ? role.privileges : role.privileges ? role.privileges.split(",") : [];
          const categoryCounts = {};
          Object.entries(privilegeLabels).forEach(([category, privs]) => {
            const count = privileges.filter((p) => Object.keys(privs).includes(p.trim())).length;
            if (count > 0) {
              const label = category.charAt(0).toUpperCase() + category.slice(1);
              categoryCounts[label] = count;
            }
          });

          return (
            <div className="privilege-badges-scrollable">
              {Object.entries(categoryCounts).map(([cat, count]) => (
                <span key={cat} className="badge blue mr-5">
                  {cat} ({count})
                </span>
              ))}
            </div>
          );
        },
      },
      {
        key: "userCount",
        header: "# of Users Assigned",
        className: "col-small",
        template: (role) => <span className="main-row">{role.userCount ?? 0}</span>,
      },
    ],
    onClickRow: (role) => {
      setSelectedRole(role);
      setShowModalEdit(true);
    },
  };

  const filteredRoles = roles.filter((role) => {
    if (!searchText) return true;
    const text = searchText.toLowerCase();

    if (role.name && role.name.toLowerCase().includes(text)) return true;

    return false;
  });

  return (
    <>
      <div className="page-wrapper settings-container">
        <div className="btn-bar">
          <div className="btn-bar-left">
            <div className="title">Role Management</div>
          </div>
          <div className="btn-bar-right">
            <SearchBar value={searchText} onChange={(v) => setSearchText(v)} placeholder="Search" />
          </div>
        </div>

        <div className="table-container">
          <Table config={config} data={filteredRoles} rowIcon="key" showFooter={true} paginationCountText="TOTAL ROLES: " />
        </div>
      </div>

      {showModalEdit && (
        <RoleEdit
          roleInit={selectedRole}
          onClose={() => setShowModalEdit(false)}
          onUpdate={(updatedRole) => {
            setRoles((prev) => {
              const exists = prev.find((role) => role.id === updatedRole.id);
              if (exists) {
                // Edit
                return prev.map((role) => (role.id === updatedRole.id ? updatedRole : role));
              } else {
                // Create
                return [...prev, updatedRole];
              }
            });
          }}
        />
      )}
    </>
  );
}
